import type { OpenTabSummary } from '@gc/shared';

/**
 * The cafe's Visitor card as `GET /tabs/live` answers it — an open-tab summary
 * plus what the party owes and whether anyone is playing.
 *
 * Mirrors `LiveTabSummary` in the API's `tabs/tab-housekeeping.service.ts`;
 * both belong in `@gc/shared` beside `OpenTabSummary` once packages unfreeze.
 */
export interface LiveTabSummary extends OpenTabSummary {
  /** Settling the whole tab right now — the Outstanding screen's figure. */
  owedMd: string;
  running: boolean;
}

/** A row of `GET /tabs/empty`, what "Close empty tabs" lists. */
export interface EmptyTabRow {
  tabId: string;
  name: string;
  customerAccountId: string | null;
  openedAt: string;
  sittingCount: number;
  paidBillCount: number;
}

export interface EmptyTabsResponse {
  tabs: EmptyTabRow[];
  asOf: string;
}

export interface CloseEmptyTabsResult {
  closed: number;
  closedTabIds: string[];
  skipped: Array<{ tabId: string; reason: 'not_open' | 'not_empty' }>;
}

/** Which list a picker reads: every open tab (merges), or only the live ones (the cafe). */
export type OpenTabSource = 'open' | 'live';

export function isLiveTab(tab: OpenTabSummary): tab is LiveTabSummary {
  return typeof (tab as Partial<LiveTabSummary>).owedMd === 'string';
}

/**
 * The money a card shows: what the party OWES when the server says (the live
 * list), otherwise the unsettled sittings the open list has always carried.
 */
export function cardAmountMd(tab: OpenTabSummary): string {
  return isLiveTab(tab) ? tab.owedMd : tab.runningTotalMd;
}

/**
 * Names are matched the way a cashier types them: any case, any spacing.
 * "yaran  staff" finds 'Yaran staff' (owner, 2026-09-23 — the two tabs that
 * started this differed only in the capital Y). Locale-aware lowering, so a
 * Kurdish or Arabic name is untouched and a Latin one folds.
 */
export function foldName(name: string): string {
  return name.trim().replace(/\s+/g, ' ').toLocaleLowerCase();
}

export function filterTabs<T extends OpenTabSummary>(
  tabs: readonly T[],
  search: string,
  excludeTabId?: string | null,
): T[] {
  const needle = foldName(search);
  return tabs
    .filter((tab) => tab.id !== excludeTabId)
    .filter((tab) => needle === '' || foldName(tab.name).includes(needle));
}

/**
 * How many OTHER cards on the list carry the same name, case-insensitively.
 * A card with a twin shows when it was opened prominently: that date and the
 * amount are what tell the two apart.
 */
export function sameNameCounts(tabs: readonly OpenTabSummary[]): Map<string, number> {
  const byName = new Map<string, number>();
  for (const tab of tabs) byName.set(foldName(tab.name), (byName.get(foldName(tab.name)) ?? 0) + 1);
  return new Map(tabs.map((tab) => [tab.id, (byName.get(foldName(tab.name)) ?? 1) - 1]));
}

/**
 * "Opened 14:30" for today, "Opened 17 Sep" otherwise — the opened date is
 * the one fact that tells a six-week-old house tab from tonight's.
 */
export function openedLabelParts(
  openedAt: string,
  now: Date,
  locale: string,
): { today: boolean; text: string } {
  const opened = new Date(openedAt);
  const today = opened.toDateString() === now.toDateString();
  const text = today
    ? opened.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })
    : opened.toLocaleDateString(locale, {
        day: 'numeric',
        month: 'short',
        ...(opened.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}),
      });
  return { today, text };
}
